// useClickNudge.js
import { useEffect, useState, useCallback } from 'react';

const NUDGE_DISABLED_KEY = 'clickNudgeDisabled';   // session opt-out
const NUDGE_COUNT_KEY = 'clickNudgeCount';         // per-session counter

/**
 * useClickNudge
 * - Counts global clicks and shows overlay whenever the threshold is reached.
 * - Dismiss `[×]` hides overlay and resets the counter (can reappear later).
 * - "Don't show again" disables for the current session (resets after tab close).
 */
export default function useClickNudge({
  isEnabled,
  threshold = 20,
}) {
  // check sessionStorage (not localStorage) for disable flag
  const [nudgeDisabled, setNudgeDisabled] = useState(
    () => sessionStorage.getItem(NUDGE_DISABLED_KEY) === '1'
  );
  const [showClickNudge, setShowClickNudge] = useState(false);

  // session counter helpers
  const getCount = () => Number(sessionStorage.getItem(NUDGE_COUNT_KEY) || 0);
  const setCount = (n) => sessionStorage.setItem(NUDGE_COUNT_KEY, String(n));
  const resetCount = () => setCount(0);

  useEffect(() => {
    if (!isEnabled || nudgeDisabled) return;

    const onDocClick = (e) => {
      // ignore clicks inside the overlay
      const withinNudge = e?.target?.closest?.('#click-nudge');
      if (withinNudge) return;

      // if overlay is visible, don't keep counting
      if (showClickNudge) return;

      const next = getCount() + 1;
      setCount(next);

      if (next >= threshold) {
        setShowClickNudge(true);
        resetCount(); // start fresh for next cycle
      }
    };

    document.addEventListener('click', onDocClick, { passive: true });
    return () => document.removeEventListener('click', onDocClick);
  }, [isEnabled, nudgeDisabled, threshold, showClickNudge]);

  // [×] — hide now, reset counter so it can re-trigger at threshold later
  const dismissNudge = useCallback(() => {
    setShowClickNudge(false);
    resetCount();
  }, []);

  // "Don't show again" — disable until tab/browser session ends
  const disableNudge = useCallback(() => {
    sessionStorage.setItem(NUDGE_DISABLED_KEY, '1');
    setNudgeDisabled(true);
    setShowClickNudge(false);
    resetCount();
  }, []);

  return {
    showClickNudge,
    nudgeDisabled,
    dismissNudge,   // for the [×] button
    disableNudge,   // for “Don’t show again”
  };
}
